/**
 * @file Resizable Column Header Component
 * @description A table header component with column resizing functionality
 */

import React from 'react';

/**
 * ResizableHeader Component
 *
 * @component
 * @description A table header cell that allows users to resize columns by dragging the right edge.
 * Features:
 * - Displays header content with optional click handler (for sorting)
 * - Shows a resize handle on the right edge
 * - Maintains column width through columnWidths prop
 * - Visual feedback on hover over resize handle
 *
 * @param {Object} props - Component props
 * @param {string} props.columnKey - Unique identifier for the column
 * @param {React.ReactNode} props.children - Header content to display
 * @param {Object} [props.style] - Additional inline styles for the header
 * @param {Function} [props.onClick] - Click handler (typically for sorting)
 * @param {Object} props.columnWidths - Map of column keys to their current widths
 * @param {Function} props.onResizeStart - Handler called when resize drag starts
 *
 * @returns {JSX.Element} Table header cell with resize handle
 *
 * @example
 * <ResizableHeader
 *   columnKey="name"
 *   style={{minWidth: '180px', cursor: 'pointer'}}
 *   onClick={() => handleSort('name')}
 *   columnWidths={columnWidths}
 *   onResizeStart={handleResizeStart}
 * >
 *   Name {sortKey === 'name' ? '↓' : '⇅'}
 * </ResizableHeader>
 */
const ResizableHeader = ({ columnKey, children, style, onClick, columnWidths, onResizeStart }) => {
  const headerWidth = columnWidths[columnKey] || style?.width;

  return (
    <th
      style={{
        ...style,
        width: headerWidth,
        minWidth: headerWidth,
        maxWidth: headerWidth,
        position: 'relative',
        borderRight: '1px solid #cbd5e1'
      }}
      onClick={onClick}
      role="columnheader"
    >
      {children}
      <div
        onMouseDown={(e) => {
          e.stopPropagation();
          onResizeStart(e, columnKey);
        }}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '8px',
          cursor: 'col-resize',
          userSelect: 'none',
          zIndex: 3,
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#94a3b8'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    </th>
  );
};

export default ResizableHeader;
